import { PropsWithChildren, useLayoutEffect, useRef, useState } from "react";
import { useMatchMedia } from "shared/UI/Hooks/useMatchMedia";
import { ResponsiveBreakpoints } from "shared/UI/ResponsiveBreakpoints";

export const ROW_HEIGHT_PX = 4;
export const ITEM_GAP_PX = 40;

export type HomeLayoutItem = {
  id: string;
  condition?: boolean;
  content: JSX.Element;
};

export const getRowSpan = (contentHeight: number) => (contentHeight > 0 ? Math.ceil((contentHeight + ITEM_GAP_PX) / ROW_HEIGHT_PX) : 1);

const BalancedItem = ({ id, children }: PropsWithChildren<{ id: string }>) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }
    const measure = () => setContentHeight(content.getBoundingClientRect().height);
    measure();

    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      data-testid={`home-layout-item-${id}`}
      className="min-w-0"
      style={{ gridRowEnd: `span ${getRowSpan(contentHeight)}`, paddingBottom: contentHeight > 0 ? ITEM_GAP_PX : 0 }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

export const HomeBalancedLayout = ({ items }: { items: HomeLayoutItem[] }) => {
  const isDesktop = useMatchMedia(ResponsiveBreakpoints.Desktop);
  const visibleItems = items.filter(({ condition = true }) => condition);

  if (!isDesktop) {
    return (
      <div className="flex flex-col gap-10">
        {visibleItems.map(({ id, content }) => (
          <div key={id} data-testid={`home-layout-item-${id}`}>
            {content}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div data-testid="home-balanced-layout" className="grid grid-cols-2 gap-x-20" style={{ gridAutoRows: `${ROW_HEIGHT_PX}px` }}>
      {visibleItems.map(({ id, content }) => (
        <BalancedItem key={id} id={id}>
          {content}
        </BalancedItem>
      ))}
    </div>
  );
};
